import { SamplesBrand } from 'constants/brands';
import React from 'react';
import { Group, useBreakpoint } from '@landr/maestro';
import { Trans } from '@lingui/macro';
import Slice from 'components/Slice/Slice';
import { SectionHeader } from 'components/Header/Header';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { usePricingCatalogs } from 'hooks/usePricingCatalogs/usePricingCatalogs';
import {
    BundleCatalog,
    SampleCatalog,
} from '@landr/pricing/lib/types/generated';
import { GetDiscountedPlan } from 'hooks/useGetDiscountedPlan/useGetDiscountedPlan';
import NeedMoreSamples from './components/NeedMoreSamples';
import PricingCard from './components/PricingCard';
import { samplesPricing, studioPricing } from './content';
import { PricingCardContent } from './types';
import { getSamplesPrice } from './helpers/getSamplesPrice';
import { getStudioPrice } from './helpers/getStudioPrice';

export const usePricingCards = ({
    studioCatalog,
    samplesCatalog,
    getDiscountedPlan,
}: {
    studioCatalog: BundleCatalog | undefined;
    samplesCatalog: SampleCatalog | undefined;
    getDiscountedPlan: GetDiscountedPlan;
}) => {
    const { hasFetched, hasStudioPlan, hasSamplesPlan } =
        useSubscriptionsAndAllowedFeaturesContext();
    const isMobile = useBreakpoint('md');

    const showSampleCard = !(hasSamplesPlan || hasStudioPlan);
    const showStudioCard = !hasStudioPlan;
    const showHeading = showSampleCard || showStudioCard;
    const showNeedMoreCredit = !showSampleCard;

    const { samplesPriceWithCurrency, samplesSalesWithCurrency } =
        getSamplesPrice({ samplesCatalog, getDiscountedPlan });

    const { studioPriceWithCurrency, studioSalesWithCurrency } = getStudioPrice(
        { studioCatalog, getDiscountedPlan },
    );

    const samplesContent = samplesPricing(
        samplesSalesWithCurrency || samplesPriceWithCurrency,
    );
    const studioContent = studioPricing(
        studioSalesWithCurrency || studioPriceWithCurrency,
    );

    return {
        hasFetched,
        showHeading,
        showSampleCard,
        showStudioCard,
        showNeedMoreCredit,
        isMobile,
        samplesContent,
        studioContent,
    };
};

const DefaultHeading = () => {
    const isMobile = useBreakpoint('md');

    return (
        <SectionHeader
            size={isMobile ? 'xl' : 'xxxl'}
            mb={isMobile ? 'xxl' : 'jb'}
        >
            <Trans>Get {SamplesBrand} credits</Trans>
        </SectionHeader>
    );
};

export const _PricingCards = ({
    showHeading,
    showSampleCard,
    showStudioCard,
    showNeedMoreCredit,
    isMobile,
    isLoading,
    samplesContent,
    studioContent,
    header: Header = DefaultHeading,
}: {
    showHeading: boolean;
    showSampleCard: boolean;
    showStudioCard: boolean;
    showNeedMoreCredit: boolean;
    isMobile: boolean;
    isLoading: boolean;
    samplesContent: PricingCardContent;
    studioContent: PricingCardContent;
    header?: () => JSX.Element;
}) => (
    <>
        {showHeading && <Header />}
        <Slice>
            <Group direction="vertical" gutter="jb">
                {showSampleCard && (
                    <Group.Item>
                        <PricingCard
                            isLoading={isLoading}
                            direction="row"
                            content={samplesContent}
                            isMobile={isMobile}
                        />
                    </Group.Item>
                )}
                {showStudioCard && (
                    <Group.Item>
                        <PricingCard
                            isLoading={isLoading}
                            direction="row-reverse"
                            content={studioContent}
                            isMobile={isMobile}
                        />
                    </Group.Item>
                )}
                {showNeedMoreCredit && (
                    <Group.Item>
                        <NeedMoreSamples />
                    </Group.Item>
                )}
            </Group>
        </Slice>
    </>
);

const PricingCards = ({
    customHeader,
}: {
    customHeader?: () => JSX.Element;
}) => {
    const { studioCatalog, samplesCatalog, getDiscountedPlan } =
        usePricingCatalogs();

    const {
        hasFetched,
        showHeading,
        showSampleCard,
        showStudioCard,
        showNeedMoreCredit,
        isMobile,
        samplesContent,
        studioContent,
    } = usePricingCards({ studioCatalog, samplesCatalog, getDiscountedPlan });

    return (
        <_PricingCards
            header={customHeader}
            isLoading={!hasFetched}
            isMobile={isMobile}
            showHeading={showHeading}
            showSampleCard={showSampleCard}
            showStudioCard={showStudioCard}
            showNeedMoreCredit={showNeedMoreCredit}
            samplesContent={samplesContent}
            studioContent={studioContent}
        />
    );
};

export default PricingCards;
